import React, { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";

import { useMedia } from "@rapid/contexts/MediaContext";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { FloatingHint } from "@rapid/components/FloatingHint";
import { formatTime, ThumbnailMode } from "@rapid/utils/videoEditing";
import { MAX_VIDEO_DURATION } from "@rapid/constants/media";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import { PlayPauseButton } from "@rapid/components/file/VideoControls/PlayPauseButton";
import { useVideoTimelinePreviews } from "@rapid/hooks/useVideoTimelinePreviews";
import { useSafeVideoElement } from "@rapid/hooks/useSafeBlobUrl";
import { LoadingOverlay } from "@rapid/components/file/Preview/Loading";
import { PreviewThumbnails } from "@rapid/components/file/Preview/PreviewThumbnails";

const BOX_DRAG_THRESHOLD_PX = 4;

const THUMBNAIL_GENERATOR_OPTS = {
    size: 128,
    quality: 6,
    mode: ThumbnailMode.FAST,
    numPreviews: 8,
    defaultFramePreviews: Array(8).fill(undefined)
};

export const VideoTrimmer = () => {
    const { setCurrentTrimTimes, mediaItemToTrim, handleVideoTrim, rejectCurrentFile } = useMedia();
    const { track } = useAnalytics();
    const {
        createVideo: safeCreateVideo,
        cleanup: safeCleanup,
        manager: safeVideoManager
    } = useSafeVideoElement();

    const isOpen = useMemo(
        () => Boolean(mediaItemToTrim?.validation?.needsTrimming),
        [mediaItemToTrim?.validation?.needsTrimming]
    );

    const file = useMemo(() => mediaItemToTrim?.file, [mediaItemToTrim]);

    const totalFrames = useMemo(
        () => mediaItemToTrim?.metadata?.frame_count || 0,
        [mediaItemToTrim?.metadata]
    );
    const fileDuration = useMemo(
        () => mediaItemToTrim?.metadata?.videoMetadata?.duration || 0,
        [mediaItemToTrim?.metadata]
    );
    const keyframeOffset = useMemo(
        () => mediaItemToTrim?.metadata?.keyframe_offset || 0,
        [mediaItemToTrim?.metadata]
    );

    const isTrimming = useMemo(
        () => mediaItemToTrim?.metadata?.videoMetadata?.isTrimming || false,
        [mediaItemToTrim?.metadata]
    );

    const videoRef = useRef<HTMLVideoElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const [duration, setDuration] = useState(0);
    const [startTime, setStartTime] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [isDraggingBox, setIsDraggingBox] = useState<boolean>(false);
    const [isDraggingPlayhead, setIsDraggingPlayhead] = useState<boolean>(false);
    const [isVideoReadyForSeeking, setIsVideoReadyForSeeking] = useState(false);

    // Local dragging state for immediate visual feedback
    const [dragStartTime, setDragStartTime] = useState<number | null>(null);

    // When the user presses on the box, we wait for movement before treating it
    // as a drag — a bare click should seek the playhead instead of moving the box.
    // `offsetTime` captures how far (in time) the click was from the box's start,
    // so the box drags at the cursor's offset (not recentered on the cursor).
    const boxPressRef = useRef<{ startX: number; offsetTime: number; moved: boolean } | null>(null);

    // Use dragging state if available, otherwise use actual startTime
    const displayStartTime = dragStartTime !== null ? dragStartTime : startTime;
    const endTime = Math.min(displayStartTime + MAX_VIDEO_DURATION, duration);

    // Update context with current trim times whenever they change (but not while dragging the box)
    useEffect(() => {
        if (duration > 0 && !isDraggingBox) {
            const endTime = Math.min(startTime + MAX_VIDEO_DURATION, duration);

            setCurrentTrimTimes({ startTime, endTime });
        }
    }, [startTime, duration, setCurrentTrimTimes, isDraggingBox]);

    useEffect(() => {
        if (!file) return;

        safeCreateVideo(file);
        setVideoUrl(safeVideoManager.current.blobManager.url);

        return () => safeCleanup();
    }, [file, safeCreateVideo, setVideoUrl, safeVideoManager, safeCleanup]);

    // Use the custom hook for preview generation
    const { orderedFramePreviews } = useVideoTimelinePreviews({
        mediaItem: mediaItemToTrim,
        totalFrames,
        fileDuration,
        keyframeOffset,
        isVideoReadyForSeeking,
        setIsVideoReadyForSeeking,
        videoRef,
        thumbnailGeneratorOpts: THUMBNAIL_GENERATOR_OPTS
    });

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            const videoDuration = videoRef.current.duration;
            setDuration(videoDuration);
            // Start with the first 20 seconds
            setStartTime(0);
        }
    };

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    }, []);

    const handlePlayPause = useCallback(() => {
        setIsPlaying((prevIsPlaying) => {
            if (videoRef.current) {
                if (prevIsPlaying) {
                    videoRef.current.pause();
                } else {
                    videoRef.current.play();
                }
            }
            return !prevIsPlaying;
        });
    }, []);

    const handleSeek = useCallback((time: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            setCurrentTime(time);
        }
    }, []);

    const timeAtClientX = useCallback(
        (clientX: number) => {
            if (!timelineRef.current) return null;
            const rect = timelineRef.current.getBoundingClientRect();
            const x = clientX - rect.left;
            const percentage = Math.max(0, Math.min(x / rect.width, 1));
            return percentage * duration;
        },
        [duration]
    );

    const moveBoxByCursor = useCallback(
        (cursorTime: number) => {
            const offsetTime = boxPressRef.current?.offsetTime ?? MAX_VIDEO_DURATION / 2;
            const newStartTime = cursorTime - offsetTime;
            const maxStartTime = Math.max(0, duration - MAX_VIDEO_DURATION);
            setDragStartTime(Math.max(0, Math.min(newStartTime, maxStartTime)));
        },
        [duration]
    );

    const armBoxPress = (clientX: number) => {
        const t = timeAtClientX(clientX);
        const offsetTime = t === null ? MAX_VIDEO_DURATION / 2 : t - startTime;
        boxPressRef.current = { startX: clientX, offsetTime, moved: false };
        setIsDraggingBox(true);
    };

    const handleSelectionMouseDown = (e: React.MouseEvent) => {
        if (isTrimming) return; // Disable during trimming
        e.stopPropagation();
        // Arm a potential box drag — actual drag only starts once the cursor
        // moves past the threshold. A click without movement falls through to
        // a playhead seek on mouseup.
        armBoxPress(e.clientX);
    };

    const handleSelectionTouchStart = (e: React.TouchEvent) => {
        if (isTrimming) return; // Disable during trimming
        const touch = e.touches[0];
        if (!touch) return;
        e.stopPropagation();
        armBoxPress(touch.clientX);
    };

    const handlePlayheadMouseDown = (e: React.MouseEvent) => {
        if (isTrimming) return;
        e.stopPropagation();
        setIsDraggingPlayhead(true);
    };

    const handlePlayheadTouchStart = (e: React.TouchEvent) => {
        if (isTrimming) return;
        e.stopPropagation();
        setIsDraggingPlayhead(true);
    };

    const handleTimelineMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isTrimming) return;
        const t = timeAtClientX(e.clientX);
        if (t === null) return;
        handleSeek(t);
        setIsDraggingPlayhead(true);
    };

    const handleTimelineTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (isTrimming) return;
        const touch = e.touches[0];
        if (!touch) return;
        const t = timeAtClientX(touch.clientX);
        if (t === null) return;
        handleSeek(t);
        setIsDraggingPlayhead(true);
    };

    const passedBoxDragThreshold = (clientX: number) => {
        const press = boxPressRef.current;
        if (!press) return true;
        if (press.moved) return true;
        if (Math.abs(clientX - press.startX) < BOX_DRAG_THRESHOLD_PX) return false;
        press.moved = true;
        return true;
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            const t = timeAtClientX(e.clientX);
            if (t === null) return;
            if (isDraggingBox) {
                if (!passedBoxDragThreshold(e.clientX)) return;
                moveBoxByCursor(t);
            } else if (isDraggingPlayhead) {
                handleSeek(t);
            }
        },
        [isDraggingBox, isDraggingPlayhead, timeAtClientX, moveBoxByCursor, handleSeek]
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isDraggingBox && !isDraggingPlayhead) return;
            const touch = e.touches[0];
            if (!touch) return;
            // Prevent scrolling while dragging
            e.preventDefault();
            const t = timeAtClientX(touch.clientX);
            if (t === null) return;
            if (isDraggingBox) {
                if (!passedBoxDragThreshold(touch.clientX)) return;
                moveBoxByCursor(t);
            } else {
                handleSeek(t);
            }
        },
        [isDraggingBox, isDraggingPlayhead, timeAtClientX, moveBoxByCursor, handleSeek]
    );

    const handleDragEnd = useCallback(() => {
        if (isDraggingBox) {
            const press = boxPressRef.current;
            if (press && !press.moved) {
                // Bare click on the box — seek the playhead instead of moving the box.
                const t = timeAtClientX(press.startX);
                if (t !== null) handleSeek(t);
            } else if (dragStartTime !== null) {
                // Commit the dragged box position; do NOT move the playhead.
                setStartTime(dragStartTime);
            }
            setDragStartTime(null);
        }
        boxPressRef.current = null;
        setIsDraggingBox(false);
        setIsDraggingPlayhead(false);
    }, [isDraggingBox, dragStartTime, timeAtClientX, handleSeek]);

    useEffect(() => {
        if (isDraggingBox || isDraggingPlayhead) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleDragEnd);
            document.addEventListener("touchmove", handleTouchMove, { passive: false });
            document.addEventListener("touchend", handleDragEnd);
            document.addEventListener("touchcancel", handleDragEnd);

            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleDragEnd);
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleDragEnd);
                document.removeEventListener("touchcancel", handleDragEnd);
            };
        }
    }, [isDraggingBox, isDraggingPlayhead, handleMouseMove, handleTouchMove, handleDragEnd]);

    // Handle modal close - reject the file
    const handleCloseModal = useCallback(() => {
        if (rejectCurrentFile) {
            rejectCurrentFile();
        }
    }, [rejectCurrentFile]);

    // Video trim handler
    const onClickTrimVideo = useCallback(async () => {
        try {
            track({ event: AnalyticsFlowEvent.TRIM_STARTED });
            handleVideoTrim();
        } catch (error) {
            console.error("🎬 [VIDEO_TRIMMER] Video trim failed:", error);
        }
    }, [handleVideoTrim, track]);

    return (
        <ModalWithTitle
            isOpen={isOpen}
            onClose={handleCloseModal}
            onSubmit={onClickTrimVideo}
            submitButtonText={
                isTrimming ? (
                    "Trimming..."
                ) : (
                    <>
                        <i className="far fa-scissors" /> Upload Trimmed Video
                    </>
                )
            }
            submitButtonProps={{
                loading: isTrimming,
                loadingText: "Trimming...",
                disabled: isTrimming
            }}
            title={`Trim Video to ${MAX_VIDEO_DURATION} Seconds`}
            icon="fad fa-cut"
            size="max"
            scrollInsideModal={true}
            panelClassName="h-full"
            overlayClassName="backdrop-blur-sm"
            className="visualArea bg-gray-50"
            allowClickPropagation
            allowMouseUpPropagation
        >
            {/* Video Preview - made smaller to accommodate controls */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                className="fileCarouselWindow flex-1 overflow-hidden"
            >
                <div className="filePreview">
                    <div className="floatingHint">
                        <FloatingHint
                            isVisible={!isTrimming}
                            message={
                                isTrimming
                                    ? "Trimming..."
                                    : `Drag the selection window (${MAX_VIDEO_DURATION}s) to choose which part of your video to upload. Click the timeline or drag the playhead to preview a different frame.`
                            }
                        />
                    </div>
                    <LoadingOverlay show={isTrimming} loadingText="Trimming..." />
                    {videoUrl && (
                        <video
                            ref={videoRef}
                            src={videoUrl}
                            className={twClassNames(
                                "absolute left-0 top-0 h-full w-full object-contain transition-all duration-300"
                            )}
                            onLoadedMetadata={handleLoadedMetadata}
                            onTimeUpdate={handleTimeUpdate}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            muted
                            playsInline
                        />
                    )}
                </div>

                {/* Timeline Container - matches VideoControls styling */}
                <div
                    className={twClassNames(
                        "videoTimelineContainer pb-9",
                        isTrimming && "pointer-events-none opacity-50"
                    )}
                    style={{ touchAction: isTrimming ? "auto" : "none" }}
                >
                    <PlayPauseButton
                        isPlaying={isPlaying}
                        onClick={handlePlayPause}
                        disabled={isTrimming}
                    />

                    {/* Timeline */}
                    <div
                        ref={timelineRef}
                        className={twClassNames(
                            "videoTimeline relative select-none rounded-md transition-all duration-300",
                            isTrimming ? "cursor-not-allowed" : "cursor-pointer"
                        )}
                        onMouseDown={handleTimelineMouseDown}
                        onTouchStart={handleTimelineTouchStart}
                    >
                        {/* Preview thumbnails */}
                        <PreviewThumbnails previews={orderedFramePreviews} />

                        {/* Opaque overlay for non-selected areas */}
                        {orderedFramePreviews.length > 0 && (
                            <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-md">
                                {/* Left opaque area */}
                                <div
                                    className="absolute inset-y-0 left-0 bg-black/30"
                                    style={{ width: `${(displayStartTime / duration) * 100}%` }}
                                />
                                {/* Right opaque area */}
                                <div
                                    className="absolute inset-y-0 right-0 bg-black/30"
                                    style={{ width: `${((duration - endTime) / duration) * 100}%` }}
                                />
                            </div>
                        )}

                        {/* Clear 20s selection window with border */}
                        <div
                            className={twClassNames(
                                "bg-brand/30 absolute bottom-0 top-0 z-20 rounded-md border-b-4 border-l-[16px] border-r-[16px] border-t-4 border-brand shadow-md ring-1 ring-inset ring-white/60 transition-all duration-300",
                                isTrimming
                                    ? "cursor-not-allowed opacity-75"
                                    : "cursor-grab active:cursor-grabbing"
                            )}
                            style={{
                                left: `${(displayStartTime / duration) * 100}%`,
                                width: `${(MAX_VIDEO_DURATION / duration) * 100}%`
                            }}
                            onMouseDown={handleSelectionMouseDown}
                            onTouchStart={handleSelectionTouchStart}
                        />

                        {/* Current time indicator (playhead) — draggable, sits above the trim box */}
                        <div
                            className={twClassNames(
                                "absolute inset-y-1/2 z-30 flex h-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center px-2",
                                isTrimming
                                    ? "pointer-events-none cursor-not-allowed"
                                    : "cursor-pointer"
                            )}
                            style={{ left: `${(currentTime / duration) * 100}%` }}
                            onMouseDown={handlePlayheadMouseDown}
                            onTouchStart={handlePlayheadTouchStart}
                        >
                            <div className="currentTimeIndicator pointer-events-none h-full" />
                        </div>

                        {/* Playhead timestamp */}
                        <div
                            className="timestampContainer -top-6"
                            style={{
                                left: `${(currentTime / duration) * 100}%`,
                                transform: "translateX(-50%)"
                            }}
                        >
                            <span className="timestamp">{formatTime(currentTime)}</span>
                        </div>

                        {/* Trim selection range — purple to match the selection box */}
                        <div
                            className="timestampContainer -bottom-6"
                            style={{
                                left: `${((displayStartTime + MAX_VIDEO_DURATION / 2) / duration) * 100}%`,
                                transform: "translateX(-50%)"
                            }}
                        >
                            <span className="rounded bg-brand px-1.5 py-0.5 text-xs text-white shadow-md ring-[1px] ring-white/70 backdrop-blur">
                                {formatTime(displayStartTime)} - {formatTime(endTime)}
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>
        </ModalWithTitle>
    );
};
